import { useLayoutEffect, useRef, useState } from "react";
import { useCart, type Item } from "./shopping-cart-provider";
import { Button } from "./ui/button";
import { MessageSquareIcon, MinusIcon, PlusIcon, Trash2Icon, XIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAppLanguage } from "@/hooks/use-app-language";
import { localizedItemName, type AppLanguage } from "@/lib/app-language";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { ScrollArea } from "./ui/scroll-area";
import { CollapsibleCard } from "./collapsible-card";
import { Badge } from "./ui/badge";
import { ImageDialog } from "./preview";
import { useAuth } from "@/hooks/auth";
import { cn } from "@/lib/utils";
import { CollapsibleMachineCard } from "./machine-card";
import { useResponsive } from "@/hooks/useIsMobile";
import { Textarea } from "./ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";

const MAX_ITEM_COMMENT_LENGTH = 500;

export function CartList() {
  const cart = useCart();
  const { isLangguthUser } = useAuth();
  const { t } = useTranslation();
  const language = useAppLanguage();
  const [isConfirmingEmptyCart, setIsConfirmingEmptyCart] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const { isMobile, width } = useResponsive();
  const isExtraSmall = width < 450;

  const handleEmptyCart = () => {
    cart.emptyCart();
    setIsConfirmingEmptyCart(false);
  };

  const handleGroupExpandedChange = (groupKey: string) => (expanded: boolean) => {
    setExpandedGroups(prev => ({ ...prev, [groupKey]: expanded }));
  };

  const handleItemCommentChange = (itemId: string, comment: string) => {
    cart.updateItem(itemId, {
      comment: comment.slice(0, MAX_ITEM_COMMENT_LENGTH),
    });
  };

  if (cart.isEmpty) {
    return (
      <div className="py-6 text-center text-muted-foreground">
        {t("cart.empty")}
      </div>
    );
  }

  // Group items by machineId
  const machineGroups = [...cart.items].reduce(
    (groups, item) => {
      const machineId = item.machineId;

      if (!groups[machineId]) {
        groups[machineId] = [];
      }
      groups[machineId].push(item);
      return groups;
    },
    {} as Record<string, typeof cart.items>
  );

  // Get unique machine IDs
  const machineIds = Object.keys(machineGroups);
  const showMachineCards = machineIds.length > 1;

  // For each machine, group items by level-1 ancestor drawing number
  const machineGroupedItems = Object.keys(machineGroups).reduce((acc, machineId) => {
    const items = machineGroups[machineId];
    
    const groupedItems = items.reduce(
      (groups, item) => {
        const groupKey = item.bomItem.level1_ancestor_drawing_no || "?";

        if (!groups[groupKey]) {
          groups[groupKey] = [];
        }
        groups[groupKey].push(item);
        return groups;
      },
      {} as Record<string, typeof cart.items>
    );

    // Sort items within each group by as_mi_u_drawing_no
    Object.keys(groupedItems).forEach((groupKey) => {
      groupedItems[groupKey].sort((a, b) => {
        const drawingNoA = a.bomItem.as_mi_u_drawing_no || "";
        const drawingNoB = b.bomItem.as_mi_u_drawing_no || "";
        return drawingNoA.localeCompare(drawingNoB, undefined, { numeric: true });
      });
    });

    const sortedGroups = Object.keys(groupedItems).sort((a, b) => {
      const firstItemA = groupedItems[a][0];
      const firstItemB = groupedItems[b][0];
      const posNrA = firstItemA?.bomItem.level1_ancestor_pos_nr || "";
      const posNrB = firstItemB?.bomItem.level1_ancestor_pos_nr || "";
      return posNrA.localeCompare(posNrB, undefined, { numeric: true });
    });

    acc[machineId] = { groupedItems, sortedGroups };
    return acc;
  }, {} as Record<string, { groupedItems: Record<string, typeof cart.items>; sortedGroups: string[] }>);

  const getExpandedGroupKey = (machineId: string, groupKey: string) =>
    `${machineId}::${groupKey}`;

  // Helper function to render item groups
  const renderItemGroups = (
    machineId: string,
    groupedItems: Record<string, typeof cart.items>,
    sortedGroups: string[],
  ) => {
    return sortedGroups.map((groupKey) => {
      const expandedGroupKey = getExpandedGroupKey(machineId, groupKey);

      return (
      <CollapsibleCard
        key={expandedGroupKey}
        className="min-w-0 w-full overflow-hidden"
        unboundedContent
        title={
          <div className="flex items-center gap-1 sm:gap-2 min-w-0 overflow-hidden">
            <Badge
              variant="secondary"
              className={cn(
                "font-semibold px-2 py-1 flex-shrink-0",
                isExtraSmall ? "text-xs" : isMobile ? "text-sm" : "text-base"
              )}
            >
              {groupKey}
            </Badge>
            {groupedItems[groupKey].length > 0 && (
              <span className={cn(
                "text-muted-foreground truncate flex-1 min-w-0",
                isExtraSmall ? "text-xs" : isMobile ? "text-sm" : "text-base"
              )}>
                {localizedItemName(
                  language,
                  groupedItems[groupKey][0].bomItem.level1_ancestor_name_de,
                  groupedItems[groupKey][0].bomItem.level1_ancestor_name_en,
                )}
              </span>
            )}
            <span className={cn(
              "text-muted-foreground ml-1 sm:ml-2 flex-shrink-0",
              isExtraSmall ? "text-xs" : "text-xs md:text-sm"
            )}>
              {groupedItems[groupKey].reduce(
                (total, item) => total + (item.quantity || 1),
                0
              )} {t("cart.items")}
            </span>
          </div>
        }
        expanded={expandedGroups[expandedGroupKey] ?? true}
        onExpandedChange={handleGroupExpandedChange(expandedGroupKey)}
        collapsible
        hasChildren={groupedItems[groupKey].length > 0}
        level={showMachineCards ? 1 : 0}
        showHierarchy={false}
        showSeparator={true}
      >
        <div className="space-y-1 min-w-0">
          {groupedItems[groupKey].map((item, index) => (
            <CartPositionRow
              key={item.id}
              item={item}
              index={index}
              isLangguthUser={isLangguthUser}
              isMobile={isMobile}
              isExtraSmall={isExtraSmall}
              language={language}
              onCommentChange={(comment) => handleItemCommentChange(item.id, comment)}
              onQuantityChange={(quantity) => cart.updateItemQuantity(item.id, quantity)}
              onRemove={() => cart.removeItem(item.id)}
            />
          ))}
        </div>
      </CollapsibleCard>
      );
    });
  };

  return (
    <div className="h-full min-w-0 w-full flex flex-col overflow-x-hidden">
      <ScrollArea className="flex-1 min-h-0 min-w-0" variant="prominent" fadeEdges>
        <div className="pr-1 md:pr-2 space-y-3 min-w-0 pb-2">
          {showMachineCards ? (
            // Show machine cards with nested item groups
            machineIds.map((machineId) => {
              const machineItems = machineGroups[machineId];
              const totalItemCount = machineItems.reduce(
                (total, item) => total + (item.quantity || 1),
                0
              );
              
              return (
                <CollapsibleMachineCard 
                  key={machineId} 
                  machineId={machineId}
                  itemCount={totalItemCount}
                  itemCountLabel={t("cart.items")}
                >
                  <div className="space-y-3">
                    {renderItemGroups(
                      machineId,
                      machineGroupedItems[machineId].groupedItems,
                      machineGroupedItems[machineId].sortedGroups,
                    )}
                  </div>
                </CollapsibleMachineCard>
              );
            })
          ) : (
            // Show only item groups (single machine)
            renderItemGroups(
              machineIds[0],
              machineGroupedItems[machineIds[0]]?.groupedItems || {},
              machineGroupedItems[machineIds[0]]?.sortedGroups || [],
            )
          )}
        </div>
      </ScrollArea>
      {isLangguthUser && (
        <>
          <div className="flex justify-end gap-2 sm:gap-3 md:gap-4 items-center mt-2 flex-shrink-0 pr-2 sm:pr-3 md:pr-6">
            <Button
              variant="destructive"
              size={isExtraSmall ? "sm" : isMobile ? "default" : "lg"}
              className={cn(
                "flex items-center",
                isExtraSmall ? "px-2 py-1.5 gap-1 text-xs" : 
                isMobile ? "px-3 py-2 gap-2" : "p-4 gap-4"
              )}
              onClick={() => setIsConfirmingEmptyCart(true)}
            >
              {t("cart.emptyCart")}
            </Button>
          </div>

          <Dialog
            open={isConfirmingEmptyCart}
            onOpenChange={setIsConfirmingEmptyCart}
          >
            <DialogContent className={cn(
              "max-h-[90dvh] overflow-y-auto",
              isExtraSmall ? "p-3" : isMobile ? "p-4" : undefined
            )}>
              <DialogHeader>
                <DialogTitle className={cn(
                  isExtraSmall ? "text-sm" : isMobile ? "text-base" : undefined
                )}>
                  {t("cart.emptyCart")}
                </DialogTitle>
                <DialogDescription className={cn(
                  isExtraSmall ? "text-xs" : isMobile ? "text-sm" : undefined
                )}>
                  {t("cart.emptyCartDescription")}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className={cn(
                isExtraSmall ? "gap-1" : isMobile ? "gap-2" : undefined
              )}>
                <Button
                  variant="ghost"
                  size={isExtraSmall ? "sm" : "default"}
                  onClick={() => setIsConfirmingEmptyCart(false)}
                >
                  {t("common.cancel")}
                </Button>
                <Button 
                  variant="destructive" 
                  size={isExtraSmall ? "sm" : "default"}
                  onClick={handleEmptyCart}
                >
                  {t("common.confirm")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}
    </div>
  );
}

function CartPositionRow({
  item,
  index,
  isLangguthUser,
  isMobile,
  isExtraSmall,
  language,
  onCommentChange,
  onQuantityChange,
  onRemove,
}: {
  item: Item;
  index: number;
  isLangguthUser: boolean;
  isMobile: boolean;
  isExtraSmall: boolean;
  language: AppLanguage;
  onCommentChange: (comment: string) => void;
  onQuantityChange: (quantity: number) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const [commentOpen, setCommentOpen] = useState(false);
  const [draftComment, setDraftComment] = useState("");
  const [commentTruncated, setCommentTruncated] = useState(false);
  const draftCommentRef = useRef("");
  const commentPreviewRef = useRef<HTMLButtonElement>(null);
  const comment = item.comment ?? "";
  const trimmedComment = comment.trim();
  const hasComment = trimmedComment.length > 0;

  useLayoutEffect(() => {
    const el = commentPreviewRef.current;
    if (!el) {
      setCommentTruncated(false);
      return;
    }

    const update = () => {
      setCommentTruncated(el.scrollWidth > el.clientWidth + 1);
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [trimmedComment, isMobile, hasComment]);

  const setDraft = (value: string) => {
    const next = value.slice(0, MAX_ITEM_COMMENT_LENGTH);
    draftCommentRef.current = next;
    setDraftComment(next);
  };

  const commitComment = () => {
    onCommentChange(draftCommentRef.current);
  };

  const openCommentEditor = () => {
    setDraft(item.comment ?? "");
    setCommentOpen(true);
  };

  const closeCommentEditor = () => {
    commitComment();
    setCommentOpen(false);
  };

  const handleCommentOpenChange = (open: boolean) => {
    if (open) {
      openCommentEditor();
      return;
    }
    closeCommentEditor();
  };

  const commentEditor = (
    <Tooltip delayDuration={400} open={commentOpen ? false : undefined}>
      <Popover open={commentOpen} onOpenChange={handleCommentOpenChange}>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className={cn(
                "relative",
                isMobile ? "h-7 w-7" : "h-8 w-8",
                hasComment && "text-primary"
              )}
              aria-label={hasComment ? t("cart.itemCommentEdit") : t("cart.itemCommentAdd")}
            >
              <span className="relative inline-flex">
                <MessageSquareIcon className={cn(isMobile ? "h-3.5 w-3.5" : "h-4 w-4")} />
                {hasComment && (
                  <span className="absolute top-0 right-0 size-1.5 translate-x-1/3 -translate-y-1/3 rounded-full bg-green-600" />
                )}
              </span>
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <PopoverContent
          align="center"
          side="bottom"
          className="w-72 max-w-[min(18rem,calc(100vw-2rem))] p-3"
        >
          <div className="flex items-start justify-between gap-2 mb-1.5">
            <p className="text-sm font-medium">{t("cart.itemComment")}</p>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-6 w-6 -mt-0.5 -mr-1"
              onClick={closeCommentEditor}
              aria-label={t("common.done")}
            >
              <XIcon className="h-4 w-4" />
            </Button>
          </div>
          <Textarea
            value={draftComment}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitComment}
            placeholder={t("cart.itemCommentPlaceholder")}
            className="min-h-[88px] text-sm resize-y"
            maxLength={MAX_ITEM_COMMENT_LENGTH}
          />
          <div className="flex items-center justify-between gap-2 mt-2">
            <span
              className={cn(
                "text-xs text-muted-foreground",
                draftComment.length > MAX_ITEM_COMMENT_LENGTH * 0.9 && "text-amber-600",
                draftComment.length >= MAX_ITEM_COMMENT_LENGTH && "text-red-600"
              )}
            >
              {draftComment.length}/{MAX_ITEM_COMMENT_LENGTH}
            </span>
            <Button
              type="button"
              size="sm"
              onClick={closeCommentEditor}
            >
              {t("common.done")}
            </Button>
          </div>
        </PopoverContent>
      </Popover>
      <TooltipContent side="top">
        {hasComment ? t("cart.itemCommentEdit") : t("cart.itemCommentAdd")}
      </TooltipContent>
    </Tooltip>
  );

  return (
    <div
      className={cn(
        "flex items-center justify-between gap-0.5 sm:gap-2 md:gap-4 py-2 px-1 sm:px-2 rounded-lg min-w-0 w-full overflow-hidden",
        index % 2 === 0 ? "bg-muted/30" : "bg-background",
        "hover:bg-muted/50 transition-colors"
      )}
    >
      <div className="flex w-0 min-w-0 flex-1 items-center gap-0.5 overflow-hidden sm:gap-2 md:gap-4">
        <div className={cn(
          "flex-shrink-0",
          isExtraSmall ? "w-6 h-6" : isMobile ? "w-10 h-10" : "w-16 h-16"
        )}>
          <ImageDialog
            imageUrl={item.imageUrl || ""}
            itemName={localizedItemName(
              language,
              item.bomItem.nameDe,
              item.bomItem.nameEn,
            )}
          />
        </div>
        <div className={cn(
          "min-w-0 overflow-hidden",
          isLangguthUser && hasComment && !isMobile ? "shrink max-w-[38%] sm:max-w-[42%]" : "flex-1"
        )}>
          <h4 className={cn(
            "font-medium truncate block",
            isExtraSmall ? "text-xs" : isMobile ? "text-sm" : "text-sm"
          )}>
            {item.bomItem.as_mi_u_drawing_no}
          </h4>
          <p className={cn(
            "text-muted-foreground truncate block",
            isExtraSmall ? "text-xs" : isMobile ? "text-xs" : "text-sm"
          )}>
            {localizedItemName(
              language,
              item.bomItem.nameDe,
              item.bomItem.nameEn,
            )}
          </p>
        </div>
        {isLangguthUser && hasComment && !isMobile && (
          <div className="ml-2 flex w-0 min-w-0 flex-1 justify-end overflow-hidden sm:ml-8 md:ml-16">
            <div className="min-w-0 w-fit max-w-full">
              <Tooltip open={commentOpen || !commentTruncated ? false : undefined}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    ref={commentPreviewRef}
                    className={cn(
                      "block w-fit max-w-full min-w-0 overflow-hidden text-ellipsis whitespace-nowrap italic text-primary/80 hover:text-primary",
                      isExtraSmall ? "text-xs" : "text-xs"
                    )}
                    onClick={openCommentEditor}
                  >
                    {trimmedComment}
                  </button>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  align="center"
                  className="max-w-xs sm:max-w-sm whitespace-pre-wrap break-words text-left text-pretty"
                >
                  {trimmedComment}
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        )}
      </div>
      <div className={cn(
        "flex items-center gap-0 sm:gap-1 md:gap-2 flex-shrink-0 justify-end",
        isLangguthUser
          ? isMobile ? "min-w-[132px]" : "min-w-[156px]"
          : isMobile ? "min-w-[100px]" : "min-w-[120px]"
      )}>
        {isLangguthUser && commentEditor}
        <Button
          variant="outline"
          size="icon"
          className={cn(isMobile ? "h-7 w-7" : "h-8 w-8")}
          onClick={() => onQuantityChange((item.quantity ?? 1) - 1)}
          disabled={!item.quantity || item.quantity <= 1}
        >
          <MinusIcon className={cn(isMobile ? "h-3.5 w-3.5" : "h-4 w-4")} />
        </Button>
        <span className={cn(
          "text-center font-medium",
          isMobile ? "w-6" : "w-8",
          isExtraSmall ? "text-xs" : "text-sm"
        )}>
          {item.quantity}
        </span>
        <Button
          variant="outline"
          size="icon"
          className={cn(isMobile ? "h-7 w-7" : "h-8 w-8")}
          onClick={() => onQuantityChange((item.quantity ?? 1) + 1)}
        >
          <PlusIcon className={cn(isMobile ? "h-3.5 w-3.5" : "h-4 w-4")} />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "text-destructive hover:bg-destructive/10",
            isMobile ? "h-7 w-7" : "h-8 w-8"
          )}
          onClick={onRemove}
        >
          <Trash2Icon className={cn(isMobile ? "h-3.5 w-3.5" : "h-4 w-4")} />
        </Button>
      </div>
    </div>
  );
}
